import React, { useCallback, useState } from 'react';
import {
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { ConfigurationDeleteButton } from '../../components/ConfigurationDeleteButton';
import { EditPencilIcon } from '../../components/EditPencilIcon';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useConfigurationSoftDelete } from '../../hooks/useConfigurationSoftDelete';
import {
  deleteHolidayCalendar,
  getEmployeeCalendarAssignments,
  getHolidayCalendars,
} from '../../services/holidayCalendarService';
import type {
  EmployeeCalendarAssignment,
  HolidayCalendar,
} from '../../types/holidayCalendar';
import { normalizeApiError } from '../../api/apiError';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'HolidayCalendarList'>;

function employeeLabel(item: {
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  employeeNumber?: string | null;
  employeeId: number;
}): string {
  const display = item.displayName?.trim();
  if (display && display.toLowerCase() !== (item.employeeNumber || '').toLowerCase()) {
    return display;
  }
  const joined = [item.firstName, item.lastName]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ')
    .trim();
  if (joined) return joined;
  return `Employee ${item.employeeId}`;
}

function employeeSummary(calendar: HolidayCalendar): string {
  if (!calendar.employees.length) {
    return calendar.employeeCount
      ? `${calendar.employeeCount} employee(s)`
      : 'No employees assigned';
  }
  const names = calendar.employees.map(employeeLabel).filter(Boolean);
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
}

export function HolidayCalendarListScreen({ navigation }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  const canManage = auth.hasPermission(PERMISSIONS.CALENDAR_MANAGE);
  const [items, setItems] = useState<HolidayCalendar[]>([]);
  const [assignments, setAssignments] = useState<EmployeeCalendarAssignment[]>([]);
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const softDelete = useConfigurationSoftDelete();

  const load = useCallback(
    async (refresh = false) => {
      setRefreshing(refresh);
      try {
        const [page, employeeRows] = await Promise.all([
          getHolidayCalendars({ search, isActive: true }),
          getEmployeeCalendarAssignments().catch(() => []),
        ]);
        setItems(page.items);
        setAssignments(employeeRows);
        setError(null);
      } catch (caught) {
        setError(normalizeApiError(caught).message);
      } finally {
        setRefreshing(false);
      }
    },
    [search],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  const unassigned = assignments.filter(row => row.holidayCalendarId == null);

  return (
    <PermissionGuard
      permission={PERMISSIONS.CALENDAR_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader title="Holiday calendars" onBack={navigation.goBack} />
        <View style={styles.body}>
          <AppInput
            label="Search"
            value={search}
            onChangeText={setSearch}
            placeholder="Calendar name or code"
          />
          <AppButton title="Search" variant="secondary" onPress={() => load()} />
          {canManage ? (
            <AppButton
              title="Create calendar"
              onPress={() => navigation.navigate(routes.holidayCalendarCreate)}
            />
          ) : null}
          {unassigned.length ? (
            <AppCard style={styles.card}>
              <Text style={[styles.title, { color: palette.textPrimary }]}>
                Employees without a calendar
              </Text>
              <Text style={[styles.meta, { color: palette.textSecondary }]}>
                {unassigned.map(employeeLabel).filter(Boolean).join(', ')}
              </Text>
            </AppCard>
          ) : null}
          {error && items.length === 0 ? (
            <ErrorState message={error} onRetry={() => load()} />
          ) : (
            <FlatList
              data={items}
              keyExtractor={item => String(item.holidayCalendarId)}
              refreshControl={
                <RefreshControl refreshing={refreshing} onRefresh={() => load(true)} />
              }
              contentContainerStyle={styles.list}
              ListEmptyComponent={
                <EmptyState
                  title="No calendars yet"
                  message="Create a calendar and assign one or more employees."
                />
              }
              renderItem={({ item }) => (
                <AppCard style={styles.card}>
                  <View style={styles.cardHeader}>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`Open ${item.calendarName}`}
                      onPress={() =>
                        navigation.navigate(routes.officeCalendar, {
                          holidayCalendarId: item.holidayCalendarId,
                          calendarName: item.calendarName,
                        })
                      }
                      style={{ flex: 1 }}
                    >
                      <Text style={[styles.title, { color: palette.textPrimary }]}>
                        {item.calendarName}
                      </Text>
                      <Text style={[styles.meta, { color: palette.textSecondary }]}>
                        {item.calendarCode}
                        {item.isDefault ? ' · Default' : ''}
                        {` · ${item.calendarYear}`}
                      </Text>
                      <Text style={[styles.meta, { color: palette.textSecondary }]}>
                        Employees: {employeeSummary(item)}
                      </Text>
                    </Pressable>
                    {canManage ? (
                      <View style={styles.actions}>
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel="Edit"
                          hitSlop={8}
                          onPress={() =>
                            navigation.navigate(routes.holidayCalendarEdit, {
                              holidayCalendarId: item.holidayCalendarId,
                            })
                          }
                          style={styles.editHit}
                        >
                          <EditPencilIcon color={palette.primary} size={22} />
                        </Pressable>
                        <ConfigurationDeleteButton
                          placement="inline"
                          entityLabel="calendar"
                          loading={softDelete.busy}
                          disabled={softDelete.busy}
                          onPress={() =>
                            softDelete.confirmAndDelete({
                              entityLabel: 'calendar',
                              recordName: item.calendarName,
                              deleteRecord: () =>
                                deleteHolidayCalendar(item.holidayCalendarId),
                              onSuccess: () => {
                                load(true).catch(() => undefined);
                              },
                              errorMessage: caught =>
                                normalizeApiError(caught).message ||
                                'Unable to delete calendar.',
                            })
                          }
                        />
                      </View>
                    ) : null}
                  </View>
                </AppCard>
              )}
            />
          )}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  body: { flex: 1, padding: spacing.md, gap: spacing.sm },
  list: { gap: spacing.sm, paddingBottom: spacing.xl },
  card: { gap: spacing.xs },
  cardHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  title: typography.sectionTitle,
  meta: { ...typography.caption },
  actions: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  editHit: { padding: spacing.xs },
});
